import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Inbox,
    ClipboardList,
    Briefcase,
    UserPlus,
    GraduationCap,
    ArrowRight,
    Activity,
} from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ComplianceTile from "@/components/ComplianceTile";

const METRICS = [
    {
        key: "new_leads",
        label: "New leads",
        icon: Inbox,
        sub: "From the marketing site contact form",
        link: "/leads?status=new",
        cta: "View leads",
    },
    {
        key: "estimates_this_week",
        label: "Estimates this week",
        icon: ClipboardList,
        sub: "Created in the last 7 days",
        link: "/estimates",
        cta: "View estimates",
    },
    {
        key: "active_jobs",
        label: "Active jobs",
        icon: Briefcase,
        sub: "Scheduled or in progress",
        link: "/jobs",
        cta: "View jobs",
    },
    {
        key: "users_pending_onboarding",
        label: "Pending onboarding",
        icon: UserPlus,
        sub: "Crew/contractors not yet complete",
        link: "/team",
        cta: "View team",
    },
];

function formatRelative(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
}

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        api.get("/admin/dashboard")
            .then((r) => alive && setData(r.data))
            .catch((e) => alive && setErr(apiErrorMessage(e)));
        return () => {
            alive = false;
        };
    }, []);

    const metrics = data?.metrics || {};
    const activity = data?.activity || [];

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Dashboard</p>
                <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
                    <div>
                        <h1 className="h1" data-testid="dashboard-title">
                            Good to see you, {user?.name?.split(" ")[0] || "there"}.
                        </h1>
                        <p className="text-muted mt-2 max-w-[640px]">
                            Here's what's moving through the shop today. Jump straight into the
                            queue from any tile.
                        </p>
                    </div>
                    <Link
                        to="/leads"
                        className="btn btn-primary"
                        data-testid="dashboard-cta-leads"
                    >
                        View incoming leads
                        <ArrowRight size={16} />
                    </Link>
                </div>
            </header>

            {err && (
                <div
                    className="brand-card mt-6"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="dashboard-error"
                >
                    {err}
                </div>
            )}

            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-8">
                {METRICS.map((m, idx) => {
                    const Icon = m.icon;
                    const value = metrics[m.key];
                    return (
                        <div
                            key={m.key}
                            className={`brand-card fade-up delay-${(idx % 4) + 1}`}
                            data-testid={`metric-${m.key}`}
                        >
                            <div className="flex items-start gap-4">
                                <div className="metric-icon">
                                    <Icon size={20} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="eyebrow" style={{ color: "var(--brand-muted)" }}>
                                        <span style={{ background: "none" }}></span>
                                        {m.label}
                                    </p>
                                    <p
                                        className="font-display"
                                        style={{
                                            fontSize: "2.25rem",
                                            fontWeight: 800,
                                            letterSpacing: "-0.02em",
                                            lineHeight: 1.05,
                                            marginTop: "0.35rem",
                                            color: "var(--brand-dark)",
                                        }}
                                        data-testid={`metric-value-${m.key}`}
                                    >
                                        {value ?? "—"}
                                    </p>
                                    <p className="text-muted text-sm mt-1">{m.sub}</p>
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-[var(--brand-surface)]">
                                {m.disabled ? (
                                    <span className="text-sm text-muted">{m.cta}</span>
                                ) : (
                                    <Link to={m.link} className="link-red text-sm inline-flex items-center gap-1">
                                        {m.cta}
                                        <ArrowRight size={14} />
                                    </Link>
                                )}
                            </div>
                        </div>
                    );
                })}
            </section>

            <ComplianceTile metrics={metrics} />

            <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="brand-card lg:col-span-2 fade-up" data-testid="activity-card">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <p className="eyebrow">Recent activity</p>
                            <h2 className="h2 mt-2">Last 10 actions</h2>
                        </div>
                        <Activity size={20} style={{ color: "var(--brand-red)" }} />
                    </div>
                    <ul className="mt-6 flex flex-col gap-3" data-testid="activity-list">
                        {activity.length === 0 && (
                            <li className="text-muted text-sm">
                                Nothing has happened yet. Submissions, status changes, and team
                                updates will show up here.
                            </li>
                        )}
                        {activity.map((a) => (
                            <li
                                key={a.id}
                                className="flex items-start justify-between gap-4 pb-3 border-b border-[var(--brand-surface)] last:border-b-0"
                            >
                                <div className="flex items-start gap-3 min-w-0">
                                    <span
                                        className="w-2 h-2 rounded-full mt-2 shrink-0"
                                        style={{ background: "var(--brand-red)" }}
                                    />
                                    <p className="text-sm text-[var(--brand-dark)]">{a.summary}</p>
                                </div>
                                <span className="text-xs text-muted shrink-0">
                                    {formatRelative(a.created_at)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="brand-card fade-up delay-2" data-testid="quick-actions-card">
                    <p className="eyebrow">Quick actions</p>
                    <h2 className="h2 mt-2">Jump in</h2>
                    <div className="mt-5 flex flex-col gap-3">
                        <Link to="/leads" className="btn btn-secondary justify-start">
                            <Inbox size={16} />
                            Manage leads
                        </Link>
                        {(user?.role === "admin" || user?.role === "supervisor") && (
                            <Link to="/team" className="btn btn-secondary justify-start">
                                <UserPlus size={16} />
                                Add a crew member
                            </Link>
                        )}
                        <Link to="/settings" className="btn btn-secondary justify-start">
                            <GraduationCap size={16} />
                            Company settings
                        </Link>
                    </div>
                    <p className="text-muted text-sm mt-6">
                        Estimates, jobs, onboarding and training portals are landing in the
                        next phases.
                    </p>
                </div>
            </section>
        </div>
    );
}
